/**
 * ContributionModal — signed-in users layer stories/photos/facts onto
 * any location's category. Content stored with source="community:{uid}"
 * and sorted after Wikipedia + AI content.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { X, Send } from "lucide-react";

const CATS = [
  { id: "history",   label: "History" },
  { id: "culture",   label: "Culture" },
  { id: "nature",    label: "Nature" },
  { id: "business",  label: "Business" },
  { id: "people",    label: "People" },
  { id: "events",    label: "Events" },
  { id: "landmarks", label: "Landmarks" },
  { id: "food",      label: "Food" },
];

export default function ContributionModal({
  open,
  onClose,
  locationId,
  locationName,
  defaultCategory = "history",
  accent = "#4f46e5",
  onSubmitted,
}) {
  const [category, setCategory] = useState(defaultCategory);
  const [title, setTitle]       = useState("");
  const [summary, setSummary]   = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token") || "";
      const r = await axios.post(
        `${API}/wp/community/${locationId}/contribute`,
        {
          category,
          title: title.trim(),
          summary: summary.trim(),
          image_url: imageUrl.trim() || undefined,
          source_url: sourceUrl.trim() || undefined,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      onSubmitted?.(r.data.item);
      onClose();
      setTitle(""); setSummary(""); setImageUrl(""); setSourceUrl("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="wp-contribute-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: accent }}>
              Contribute
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mt-1">
              Add a story about {locationName}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="wp-contribute-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="wp-contribute-category"
            >
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required minLength={3} maxLength={140}
              placeholder="e.g. The Saint Mary's cathedral bell tower"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="wp-contribute-title"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Story (40+ chars)</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              required minLength={40} maxLength={2000}
              rows={5}
              placeholder="What makes this place special? Facts, memories, local knowledge..."
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm resize-none"
              data-testid="wp-contribute-summary"
            />
            <div className="text-[10px] text-slate-400 text-right mt-1">
              {summary.length}/2000
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Photo URL (optional)</label>
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="wp-contribute-image"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Source / citation URL (optional)</label>
            <input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://..."
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="wp-contribute-source"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50"
              style={{ background: accent }}
              data-testid="wp-contribute-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Submitting…" : "Publish contribution"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
